import { useDeleteFilesMutation } from '@gen2/api/files/hooks';
import { TInviteRequest } from '@gen2/api/invite-requests/api';
import {
  TemplateKeys,
  useCreateTemplateRequestMutation,
  useDeleteTemplateRequestMutation,
  useDuplicateTemplateGroupRequestMutation,
  useGetTemplateRequestGroupsQuery,
  useGetTemplateRequestsQuery,
  useUpdateTemplateRequestGroupNameMutation,
} from '@gen2/api/templates/hooks';
import { ActionModal } from '@gen2/app/components/action-modal/action-modal';
import { ConfirmationModal } from '@gen2/app/components/confirmation-modal/confirmation-modal';
import { useConfirmationModalStore } from '@gen2/app/components/confirmation-modal/hooks/confirmation-modal-store';
import { EditableTitle } from '@gen2/app/components/editable-title/editable-title';
import { useEditableTitle } from '@gen2/app/components/editable-title/useEditableTitle';
import { CreateRequest } from '@gen2/app/components/invite-request/create-request/create-request';
import { useFileStore } from '@gen2/app/components/invite-request/hooks/file-store';
import { useFormModalStore } from '@gen2/app/components/invite-request/hooks/modal-store';
import { InviteRequest } from '@gen2/app/components/invite-request/invite-request';
import UpgradeModal from '@gen2/app/components/modals/upgrade-modal';
import { useOpenSharedDrawer } from '@gen2/app/components/organizing-request-modal/hooks/useOpenSharedDrawer';
import { OrganizeRequestButton } from '@gen2/app/components/organizing-request-modal/organize-request-button';
import { validateGroupName } from '@gen2/app/components/organizing-request-modal/utils';
import {
  TInviteRequestGroup,
  TInviteRequestsForm,
  TInviteRequestsListProps,
} from '@gen2/app/invites/send-invites/requests/requests';
import {
  StyledRequests,
  StyledRequestsHead,
} from '@gen2/app/invites/send-invites/requests/requests.styled';
import { inviteRequestsSchema } from '@gen2/app/invites/send-invites/requests/schema';
import { useRequestsStore } from '@gen2/app/invites/send-invites/requests/store';
import { queryClient } from '@gen2/config';
import { useToast } from '@gen2/hooks';
import { PermissionsKeys } from '@gen2/types/permissions';
import { Permissions } from '@gen2/utils/permissions/permissions';
import { yupResolver } from '@hookform/resolvers/yup';
import { AxiosResponse } from 'axios';
import React, { useCallback, useEffect, useMemo } from 'react';
import {
  FormProvider,
  useFieldArray,
  useForm,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useUnmount } from 'usehooks-ts';
import { MAX_TEMPLATE_REQUESTS } from '../schema';
import { useTemplateFormStore } from '../store';
import { RequestList } from './template-requests.styled';

export const TEMPLATE_INITIAL_REQUEST_TITLE = 'New File Upload Request';

export const TemplateRequestsList = ({
  data,
  title,
  hasDelete,
  isGroup,
  groupId,
  onUpdateGroupTitle,
  onDuplicateGroup,
}: TInviteRequestsListProps) => {
  const { fields: requests } = useFieldArray({
    name: 'requests',
  });
  const {
    toDeleteRequest,
    setToDeleteRequest,
    expandedGroups,
    toggleGroupExpansion,
  } = useRequestsStore();
  const {
    mutate: deleteTemplateRequestMutation,
    isLoading: isRequestDeleting,
  } = useDeleteTemplateRequestMutation();
  const { setIsConfirmationModalOpen, setConfirmationModalProps } =
    useConfirmationModalStore();
  const toast = useToast();
  const { t } = useTranslation('sendInvite');
  const { templateId } = useParams<{
    templateId: string;
  }>();
  const { file: selectedFile, setFile: setSelectedFile } = useFileStore();
  const { mutate: deleteFilesMutation, isLoading: isFileDeleting } =
    useDeleteFilesMutation();

  const { getValues, reset } = useFormContext<TInviteRequestsForm>();
  // Use useWatch to only watch the expanded fields, preventing re-renders on other field changes
  const watchedRequests = useWatch<TInviteRequestsForm>({
    name: 'requests',
  }) as TInviteRequest[];
  const expandedStates =
    watchedRequests?.map((req: any) => req?.expanded) || [];

  // Get current requests data without watching to avoid re-renders
  const getCurrentRequests = useCallback(() => {
    return getValues('requests');
  }, [getValues]);

  const editableTitle = useEditableTitle({
    title,
    isGroup,
    groupId,
    onUpdateGroupTitle,
    validateGroupName,
  });

  const onDelete = useCallback(() => {
    if (toDeleteRequest) {
      // delete template request
      deleteTemplateRequestMutation(
        {
          id: toDeleteRequest.id ?? '',
          templateId: templateId ?? '',
        },
        {
          onSuccess: async () => {
            await queryClient.invalidateQueries({
              queryKey: [TemplateKeys.getTemplateRequests],
            });

            await queryClient.invalidateQueries({
              queryKey: [TemplateKeys.getTemplateRequestGroups],
            });
            toast.show({
              text: t('request.delete.success'),
              variant: 'success',
            });

            return null;
          },
          onError: () => {
            toast.show({
              text: t('request.delete.error'),
              variant: 'error',
            });
          },
          onSettled: () => {
            setToDeleteRequest(undefined);
            setIsConfirmationModalOpen(false);
          },
        },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    deleteTemplateRequestMutation,
    toDeleteRequest,
    setIsConfirmationModalOpen,
    toast,
  ]);

  const { closeCreateFormModal } = useFormModalStore();

  // Calculate expand all button state
  const expandAllButtonState = useMemo(
    () =>
      expandedStates.some((expanded: boolean) => expanded === false)
        ? 'expand'
        : 'collapse',
    [expandedStates],
  );

  // Handle expand/collapse all requests in the group
  const handleExpandCollapseAll = useCallback(() => {
    if (!groupId) return;

    const currentRequests = watchedRequests;
    if (!currentRequests?.length) return;

    const allRequestsExpanded = currentRequests.every(
      (req: any) => req.expanded === true,
    );

    // Simple toggle: if all are expanded, collapse all; otherwise expand all
    const newExpandedState = !allRequestsExpanded;

    // Update group expansion state
    toggleGroupExpansion(groupId);

    // Update all individual expanded states
    const updatedRequests = currentRequests.map((req: any) => ({
      ...req,
      expanded: newExpandedState,
    }));

    reset({ requests: updatedRequests });
  }, [groupId, expandedGroups, watchedRequests, reset, toggleGroupExpansion]);

  const requestTitle = useMemo(() => {
    if (requests.length > 0) {
      return toDeleteRequest?.title ?? '';
    }

    return '';
  }, [toDeleteRequest, requests]);

  // Memoize request components
  const memoizedRequests = useMemo(() => {
    const currentRequests = watchedRequests;

    if (!currentRequests || currentRequests.length === 0) return null;

    return currentRequests.map((request: TInviteRequest, index: number) => {
      return (
        <InviteRequest
          key={request?.id || `request-${index}`}
          id={index}
          container="template"
          hasDelete={hasDelete}
          isDeleteDisabled={isRequestDeleting}
          isGroupExpanded={groupId ? expandedGroups.has(groupId) : false}
        />
      );
    });
  }, [
    requests,
    hasDelete,
    isRequestDeleting,
    groupId,
    expandedGroups,
    watchedRequests,
  ]);

  const handleFileDelete = useCallback(() => {
    if (selectedFile) {
      deleteFilesMutation(
        {
          type: 'request',
          typeId: selectedFile.request.id,
          files: [{ id: selectedFile.id }],
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({
              queryKey: [TemplateKeys.getTemplateRequests],
            });
            queryClient.invalidateQueries({
              queryKey: [TemplateKeys.getTemplateRequestGroups],
            });
            toast.show({
              text: t('file.delete.success'),
              variant: 'success',
            });

            closeCreateFormModal();
          },
          onError: (err: any) => {
            toast.show({
              text: t('file.delete.error'),
              variant: 'error',
            });
          },
          onSettled: () => {
            setSelectedFile(undefined);
            setIsConfirmationModalOpen(false);
          },
        },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFile, toast]);

  useEffect(() => {
    // Always update form when data changes, even if empty
    if (!data) return;

    const currentRequests = getCurrentRequests();
    const currentExpansionStates =
      currentRequests?.reduce(
        (acc, req, index) => {
          if (req.expanded !== undefined) {
            acc[index] = req.expanded;
          }
          return acc;
        },
        {} as Record<number, boolean>,
      ) || {};

    const requestsWithPreservedExpansion = data.map((req, index) => ({
      ...req,
      expanded: currentExpansionStates[index] ?? (req as any).expanded ?? false,
    }));

    reset({ requests: requestsWithPreservedExpansion });
  }, [templateId, data, reset, getCurrentRequests]);

  useUnmount(() => {
    // Cleanup logic here
    reset({ requests: [] });
  });

  useEffect(() => {
    if (!requestTitle) return;

    setConfirmationModalProps({
      onClose: () => setIsConfirmationModalOpen(false),
      onSubmit: onDelete,
      isLoading: isRequestDeleting,
      title: t('request.delete.title'),
      submitLabel: t('request.delete.buttonLabel'),
      submitColor: 'danger',
      message: t('request.delete.message', {
        requestName: requestTitle,
      }),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isRequestDeleting,
    onDelete,
    requestTitle,
    setConfirmationModalProps,
    setIsConfirmationModalOpen,
  ]);

  useEffect(() => {
    if (!selectedFile) return;

    const isDeleteFileFromFormType = selectedFile.request.type === 'live-form';

    setConfirmationModalProps({
      onClose: () => setIsConfirmationModalOpen(false),
      onSubmit: handleFileDelete,
      isLoading: isFileDeleting,
      title: isDeleteFileFromFormType
        ? t('editSendInvite.file.delete.title')
        : t('file.delete.title'),
      submitLabel: t('file.delete.buttonLabel'),
      submitColor: 'danger',
      message: t('file.delete.message', {
        fileName: selectedFile.original_name,
      }),
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFileDeleting, selectedFile]);

  return (
    <StyledRequests $isGroup={isGroup}>
      <StyledRequestsHead $isGroup={isGroup}>
        <EditableTitle
          title={editableTitle.editTitle}
          isEditing={editableTitle.isEditingTitle}
          onEdit={editableTitle.handleSwitchToEditMode}
          onDuplicateGroup={() => onDuplicateGroup?.(groupId || '')}
          onExpandCollapseAll={handleExpandCollapseAll}
          onChange={editableTitle.handleTitleChange}
          onSave={editableTitle.handleTitleSave}
          error={editableTitle.editTitleError}
          helperText={editableTitle.editTitleError}
          isGroup={isGroup}
          expandAllButtonState={expandAllButtonState}
          inputProps={{ 'data-testid': 'edit-group-title-input' }}
        />
      </StyledRequestsHead>
      <RequestList>{memoizedRequests}</RequestList>
    </StyledRequests>
  );
};

export const TemplateRequests = React.memo(() => {
  const { templateId } = useParams<{
    templateId: string;
  }>();
  const { data, isLoading, isRefetching } = useGetTemplateRequestsQuery(
    templateId ?? '',
  );
  const {
    data: requestGroups,
    isLoading: isRequestGroupsLoading,
    isRefetching: isRequestGroupsRefetching,
  } = useGetTemplateRequestGroupsQuery(templateId ?? '');

  const toast = useToast();
  const { upgradeModal, setUpgradeModal } = useTemplateFormStore();
  const { mutate: createTemplateMutation, isLoading: isCreating } =
    useCreateTemplateRequestMutation();

  const { mutateAsync: updateTemplateRequestGroupNameMutation } =
    useUpdateTemplateRequestGroupNameMutation();

  const { mutateAsync: duplicateTemplateGroupRequestMutation } =
    useDuplicateTemplateGroupRequestMutation();

  const { t } = useTranslation('templates');

  const initialRequests = useMemo(
    () => data?.filter((request) => !request.request_group_id) || [],
    [data],
  );

  // Calculate total requests count (grouped + ungrouped) for delete button logic
  const totalRequestsCount = useMemo(() => {
    return data?.length || 0;
  }, [data]);

  const openSharedDrawer = useOpenSharedDrawer();

  const handleOpenDrawer = () => {
    openSharedDrawer({
      type: 'template',
      items: initialRequests,
      templateId: templateId,
      itemsToGroup: requestGroups,
    });
  };

  const onCreate = () => {
    if ((data?.length || 0) >= MAX_TEMPLATE_REQUESTS) {
      setUpgradeModal({
        isOpen: true,
        title: t('form.upgrade.title') || '',
        description: t('form.upgrade.description') || '',
      });
      return;
    }

    createTemplateMutation(
      {
        title: TEMPLATE_INITIAL_REQUEST_TITLE,
        type: 'file-upload',
        templateId: templateId ?? '',
      },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries({
            queryKey: [TemplateKeys.getTemplateRequests],
          });
        },
      },
    );
  };

  const handleUpdateGroupTitle = async (groupId: string, newTitle: string) => {
    await updateTemplateRequestGroupNameMutation({
      templateId: templateId ?? '',
      requestGroupId: groupId,
      data: { name: newTitle },
    });
  };

  const handleDuplicateGroupForTemplate = async (groupId: string) => {
    try {
      await duplicateTemplateGroupRequestMutation({
        templateId: templateId ?? '',
        requestGroupId: groupId,
      });
    } catch (e) {
      const error = e as AxiosResponse;
      if (error.status === 422) {
        toast.show({
          text: error.data?.message,
          variant: 'error',
        });
      } else {
        toast.show({
          text: 'Error duplicating invite group',
          variant: 'error',
        });
      }
    }
  };

  return (
    <>
      <Permissions allow={PermissionsKeys.REORDER_REQUEST_TEMPLATE}>
        <OrganizeRequestButton
          onClick={handleOpenDrawer}
          mt={40}
          disabled={
            isCreating ||
            isLoading ||
            isRefetching ||
            isRequestGroupsLoading ||
            isRequestGroupsRefetching
          }
          isContextReady={Boolean(templateId)}
        />
      </Permissions>
      {requestGroups &&
        requestGroups.length > 0 &&
        requestGroups.map((group: TInviteRequestGroup) => (
          <div key={group.id}>
            <TemplateRequestsListWithForm
              key={
                group.id +
                '-' +
                (group.requests?.map((r) => r.id).join(',') ?? '')
              }
              title={group.name}
              data={group.requests || []}
              hasDelete={(totalRequestsCount || 0) > 1}
              isGroup={true}
              groupId={group.id}
              onUpdateGroupTitle={handleUpdateGroupTitle}
              onDuplicateGroup={handleDuplicateGroupForTemplate}
            />
          </div>
        ))}
      {initialRequests.length > 0 && (
        <TemplateRequestsListWithForm
          key={'initial-' + initialRequests.map((r) => r.id).join(',')}
          data={initialRequests}
          hasDelete={totalRequestsCount > 1}
        />
      )}
      <CreateRequest onClick={onCreate} disabled={isCreating} />
      <UpgradeModal
        open={upgradeModal.isOpen}
        onClose={() => setUpgradeModal({ isOpen: false })}
        title={upgradeModal.title}
      >
        {upgradeModal.description}
      </UpgradeModal>
      <ConfirmationModal />
      <ActionModal />
    </>
  );
});

// New wrapper component for independent form context
const TemplateRequestsListWithForm = ({
  data,
  title,
  hasDelete,
  isGroup,
  groupId,
  onUpdateGroupTitle,
  onDuplicateGroup,
}: TInviteRequestsListProps) => {
  const methods = useForm<TInviteRequestsForm>({
    defaultValues: { requests: data },
    resolver: yupResolver(inviteRequestsSchema),
  });

  return (
    <FormProvider {...methods}>
      <TemplateRequestsList
        data={data}
        title={title}
        hasDelete={hasDelete}
        isGroup={isGroup}
        groupId={groupId}
        onUpdateGroupTitle={onUpdateGroupTitle}
        onDuplicateGroup={onDuplicateGroup}
      />
    </FormProvider>
  );
};
